/**
 * Create Listing Wizard Component
 *
 * Orchestrator: composes mixins for images, validation, packages, and draft.
 * Field names match backend spreadsheet specification exactly.
 *
 * @package Lovasok_Marketplace
 */

import { LovasokAPI } from '../utils/api.js';
import { imagesMixin }     from './mixins/listing-images.js';
import { validationMixin } from './mixins/listing-validation.js';
import { packagesMixin }   from './mixins/listing-packages.js';
import { draftMixin }      from './mixins/listing-draft.js';
import { isHorseface, requiresPaymentBeforePublish } from '../utils/listing-mode.js';

function formatUploadSize(bytes) {
    const size = Number(bytes) || 0;
    if (size >= 1048576) {
        return `${(size / 1048576).toFixed(1).replace('.0', '')} MB`;
    }
    return `${Math.max(1, Math.round(size / 1024))} KB`;
}

// URL paraméterek előzetes kiolvasása — Alpine inicializálása előtt fut,
// így az első render sem mutat hibás állapotot (nincs típusválasztó flash)
const _initParams = new URLSearchParams(window.location.search);
const _initEditId = _initParams.get('id') || null;
const _initModeParam = (_initParams.get('mode') || '').toLowerCase();

function resolveInitialListingMode(editId, modeParam) {
    if (editId) return 'elado';
    if (modeParam === 'horseface') return 'horseface';
    if (modeParam === 'elado') return 'elado';
    return '';
}

const _initialListingMode = resolveInitialListingMode(_initEditId, _initModeParam);

export default () => ({
    // ── Mixins ──────────────────────────────────────────────────────────────
    ...imagesMixin(),
    ...validationMixin(),
    ...packagesMixin(),
    ...draftMixin(),

    // ── Core state ──────────────────────────────────────────────────────────
    currentStep: 1,
    // '' = típusválasztó látszik | 'horseface' | 'elado'
    listingMode: _initialListingMode,

    verifying: false,
    passportVerified: false,

    isEditing: !!_initEditId,
    listingId: _initEditId,
    listingStatus: '',

    submitting: false,
    listingHighlightTier: 1,
    convertedFromHorseface: false,
    serverError: '',
    heightMethod: 'bot',  // 'bot' | 'szalag' — marmagasság mérési módszer

    formData: {
        listingMode: '',
        htipus: _initialListingMode === 'horseface' ? 'horseface' : 'elado',
        hirdetes_cim: '',
        lo_neve: '',
        horsefacePackage: '',  // 'monthly' | 'yearly'
        fajta_id: '',
        nem_id: '',
        foszin_id: '',
        datum_ev: '',
        datum_ho: '',
        magassag_bot: '',
        magassag_szalag: '',
        kepzettseg_id: '',
        alkalmassag: [],
        versenyez: '0',
        startszam: '',
        ve_dijugratas: false,
        ve_dijlovaglas: false,
        ve_fogathajtas: false,
        erv_loutlevel: '',
        lo_azon: '',
        tisztaveru: '',
        torzskonyvi_szam: '',
        apja: '',
        anyja: '',
        sire_sire: '',
        sire_dam: '',
        anyja_nagyapa: '',
        dam_dam: '',
        sire_sire_sire: '',
        sire_sire_dam: '',
        sire_dam_sire: '',
        sire_dam_dam: '',
        dam_sire_sire: '',
        dam_sire_dam: '',
        dam_dam_sire: '',
        dam_dam_dam: '',
        leiras: '',
        ar_st: 'ar',
        ar: '',
        penznem: 'Ft',
        arkatlista: '',
        ar_tipus: 'fix',
        csere: '0',
        weboldal: '',
        telefon: '',
        megtekint_orszag: 'HU',
        megtekint_megye: '',
        megtekint_varos: '',
        megtekint_kulfvaros: '',
        irsz: '',
        utca: '',
        package: 0,
        video_url: '',
        video_url_2: '',
        video_url_3: '',
        passportNumber: '',
    },

    // ── Computed ─────────────────────────────────────────────────────────────

    get steps() {
        if (this.listingMode === 'horseface') {
            return [
                { number: 1, name: 'Ló adatai',   icon: 'info' },
                { number: 2, name: 'Részletek',    icon: 'detail' },
                { number: 3, name: 'Képek',        icon: 'photo' },
                { number: 4, name: 'Előfizetés',   icon: 'package' },
                { number: 5, name: 'Összesítő',    icon: 'check' },
            ];
        }
        return [
            { number: 1, name: 'Alapadatok', icon: 'info' },
            { number: 2, name: 'Részletek',  icon: 'detail' },
            { number: 3, name: 'Ár & Hely', icon: 'map' },
            { number: 4, name: 'Képek',      icon: 'photo' },
            { number: 5, name: 'Csomag',     icon: 'package' },
            { number: 6, name: 'Összesítő',  icon: 'check' },
        ];
    },

    get totalSteps() { return this.steps.length; },

    // Csomag/Előfizetés lépést csak MÁR PUBLIKÁLT (kifizetett) hirdetés
    // szerkesztésekor hagyjuk ki. Draft (pl. HorseFace-ből eladóvá alakított,
    // még nem fizetett) hirdetésnél végig kell menni a csomag lépésen.
    get skipsPackageStepOnEdit() {
        return this.isEditing && this.listingStatus === 'publish';
    },

    get showCompetitionFields() { return this.formData.versenyez === '1'; },
    get showPriceField()        { return this.formData.ar_st === 'ar'; },
    get showPriceCategory()     { return this.formData.ar_st === 'arkat'; },
    get showStudFeeOption()     { return this.formData.htipus === 'fedezomen'; },
    get showPairPriceOption()   { return this.formData.htipus !== 'fedezomen'; },
    get showPedigreeFields()    { return this.formData.htipus === 'fedezomen' || this.formData.tisztaveru === 'I'; },
    get showPedigreeNumber()    { return this.formData.htipus !== 'elado'; },
    get showPassportIdentifier(){ return this.formData.erv_loutlevel === 'van'; },
    get showHungarianLocation() { return this.formData.megtekint_orszag === 'HU'; },
    get showForeignCity()       { return this.formData.megtekint_orszag !== '' && this.formData.megtekint_orszag !== 'HU'; },
    get suitabilitySelectionCount() {
        if (this.formData.htipus === 'fedezomen') {
            return this.formData.alkalmassag.filter(value => value !== 'tenyesztes').length;
        }

        return this.formData.alkalmassag.length;
    },

    get suitabilityLimitReached() { return this.suitabilitySelectionCount >= 3; },

    // ── Init ─────────────────────────────────────────────────────────────────

    async init() {
        try {
            if (typeof lovasokParams !== 'undefined' && lovasokParams.highlightProducts) {
                this.packages = Object.values(lovasokParams.highlightProducts)
                    .filter(Boolean)
                    .sort((a, b) => this.getPackageSortOrder(a) - this.getPackageSortOrder(b));
            }

            const editId = new URLSearchParams(window.location.search).get('id');

            if (editId) {
                await this.loadListing(editId);
                return;
            }

            if (new URLSearchParams(window.location.search).get('action') === 'new') {
                window.history.replaceState({}, document.title, window.location.pathname);
            }
        } finally {
            this.$nextTick(() => document.getElementById('lmk-create-listing-preload')?.remove());
        }
    },

    async loadListing(id) {
        try {
            const apiData = await LovasokAPI.get(`horses/${id}`);
            const data = apiData?.horse ? apiData.horse : apiData;

            this.formData = {
                ...this.formData,
                htipus:           data.listing_type || data.listingType || 'elado',
                hirdetes_cim:     data.listing_title || data.title || '',
                lo_neve:          data.horse_name || '',
                fajta_id:         data.breed_id || data.breed_slug || data.breed || '',
                nem_id:           data.gender_slug || data.gender || '',
                foszin_id:        data.color_id || data.color_slug || data.color || '',
                datum_ev:         data.birth_year || '',
                datum_ho:         data.birth_month || '',
                magassag_bot:     data.height_stick || '',
                magassag_szalag:  data.height_tape || '',
                kepzettseg_id:    data.training_level || data.training_slug || data.training || '',
                alkalmassag:      data.suitabilities || [],
                versenyez:        data.competes || '0',
                startszam:        data.race_number || '',
                ve_dijugratas:    data.ve_dijugratas || false,
                ve_dijlovaglas:   data.ve_dijlovaglas || false,
                ve_fogathajtas:   data.ve_fogathajtas || false,
                erv_loutlevel:    data.passport_status || '',
                lo_azon:          data.horse_identifier || '',
                tisztaveru:       data.breed_origin || '',
                torzskonyvi_szam: data.pedigree_number || '',
                apja:             data.sire || '',
                anyja:            data.dam || '',
                sire_sire:        data.sire_sire || '',
                sire_dam:         data.sire_dam || '',
                anyja_nagyapa:    data.dam_sire || '',
                dam_dam:          data.dam_dam || '',
                sire_sire_sire:   data.sire_sire_sire || '',
                sire_sire_dam:    data.sire_sire_dam || '',
                sire_dam_sire:    data.sire_dam_sire || '',
                sire_dam_dam:     data.sire_dam_dam || '',
                dam_sire_sire:    data.dam_sire_sire || '',
                dam_sire_dam:     data.dam_sire_dam || '',
                dam_dam_sire:     data.dam_dam_sire || '',
                dam_dam_dam:      data.dam_dam_dam || '',
                leiras:           data.description || '',
                ar_st:            data.price_status || 'ar',
                ar:               data.price || '',
                penznem:          data.currency || 'Ft',
                arkatlista:       data.price_category || '',
                ar_tipus:         data.price_type || 'fix',
                csere:            data.is_tradeable || '0',
                weboldal:         data.website || '',
                telefon:          data.phone || '',
                megtekint_orszag: data.country || 'HU',
                megtekint_megye:  data.county_slug || data.county || '',
                megtekint_varos:  data.city || '',
                megtekint_kulfvaros: data.foreign_city || '',
                irsz:             data.zip_code || '',
                utca:             data.street || '',
                passportNumber:   data.passport_number || '',
                video_url:        data.video_url || '',
                video_url_2:      data.video_url_2 || '',
                video_url_3:      data.video_url_3 || '',
                package:          '',
            };

            // Marmagasság mérési mód beállítása a betöltött érték alapján, hogy a
            // helyes érték jelenjen meg szerkesztéskor (szalaggal mért is).
            this.heightMethod = (!data.height_stick && data.height_tape) ? 'szalag' : 'bot';

            // Meglévő képek. A borító a galériából jön, azonosító szerint elölre téve.
            // A külön bélyegkép-URL ugyanannak a fotónak a második példánya volt.
            const existingImages = [];
            const gallery = Array.isArray(data?.images) ? data.images : (Array.isArray(data?.gallery) ? data.gallery : []);
            const featuredId = Number(data?.featuredImageId) || 0;
            const seenIds = new Set();
            gallery.forEach(img => {
                if (!img?.url) return;
                const id = Number(img.id) || 0;
                if (id && seenIds.has(id)) return;
                if (id) seenIds.add(id);
                existingImages.push({
                    url: img.url,
                    name: img.name || 'Meglévő kép',
                    id: id || null,
                    width: Number(img.width) || 0,
                    height: Number(img.height) || 0,
                });
            });
            if (featuredId) {
                existingImages.sort((a, b) => (Number(b.id) === featuredId ? 1 : 0) - (Number(a.id) === featuredId ? 1 : 0));
            } else if (existingImages.length === 0 && data?.image) {
                existingImages.push({
                    url: data.image,
                    name: 'Borítókép',
                    id: null,
                    width: 0,
                    height: 0,
                });
            }
            this.previewImages = existingImages;

            // Meglévő dokumentumok betöltése
            const docTypeLabels = {
                general:  'Dokumentum',
                xray:     'Röntgen',
                vet:      'Állatorvosi szakvélemény',
                passport: 'Lóútlevél',
                sale:     'Adásvételi szerződés',
            };
            this.previewDocuments = [];
            if (Array.isArray(data?.documents)) {
                data.documents.forEach(doc => {
                    if (doc?.url) {
                        this.previewDocuments.push({
                            id:        doc.id,
                            name:      doc.name || 'Dokumentum',
                            size:      this.formatBytes ? this.formatBytes(doc.size || 0) : '0 B',
                            url:       doc.url,
                            docType:   doc.docType || 'general',
                            typeLabel: docTypeLabels[doc.docType || 'general'] || 'Dokumentum'
                        });
                    }
                });
            }

            this.listingHighlightTier = Number(data?.highlightTier || 1);
            this.convertedFromHorseface = Boolean(data?.convertedFromHorseface);
            this.listingStatus        = data?.status || '';
            this.passportVerified     = true;

            const loadedType = this.formData.htipus || 'elado';
            this.listingMode = loadedType === 'horseface' ? 'horseface' : 'elado';
            this.ensureStudSuitability();
            this.currentStep = 1;
        } catch (error) {
            console.error('Error loading listing:', error);
            this.serverError = 'Hiba történt a hirdetés betöltése során. Kérlek töltsd újra az oldalt.';
        }
    },

    // ── Típusválasztó ─────────────────────────────────────────────────────────

    selectListingMode(mode) {
        this.listingMode = mode;
        this.formData.htipus = mode === 'horseface' ? 'horseface' : 'elado';
        this.currentStep = 1;

        try {
            const raw = localStorage.getItem(this.getDraftStorageKey());
            if (raw) {
                const draft = JSON.parse(raw);
                if (draft?.listingMode && draft.listingMode !== mode) this.clearDraft();
            } else {
                this.clearDraft();
            }
        } catch { /* ignore */ }

        window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    selectHorsefacePackage(plan) {
        this.formData.horsefacePackage = plan;
    },

    // ── Alkalmasság ──────────────────────────────────────────────────────────

    isSuitabilityLocked(value) {
        return value === 'tenyesztes' && this.formData.htipus === 'fedezomen';
    },

    ensureStudSuitability() {
        if (this.formData.htipus !== 'fedezomen') {
            return;
        }

        if (!this.formData.alkalmassag.includes('tenyesztes')) {
            this.formData.alkalmassag.push('tenyesztes');
        }
    },

    isSuitabilityChecked(value) {
        return this.formData.alkalmassag.includes(value);
    },

    isPriceTypeVisible(slug) {
        if (slug === 'fedar') return this.formData.htipus === 'fedezomen';
        if (slug === 'parban') return this.formData.htipus !== 'fedezomen';
        return true;
    },

    // ── Form change handlers ─────────────────────────────────────────────────

    onListingTypeChange() {
        if (this.formData.htipus === 'fedezomen') {
            this.formData.nem_id = 'stallion';
            if (this.formData.ar_tipus === 'parban') this.formData.ar_tipus = 'fix';
            this.ensureStudSuitability();
        }
        if (this.formData.htipus === 'elado') {
            if (this.formData.ar_tipus === 'fedar') this.formData.ar_tipus = 'fix';
            // Fedezőmén módban a nem 'Mén'-re volt zárva és a 'Tenyésztés' kötelezően
            // bepipálva. Eladó lóra visszaváltáskor ezeket a kényszerített értékeket
            // visszaállítjuk, hogy a felhasználó szabadon választhasson.
            if (this.formData.nem_id === 'stallion') {
                this.formData.nem_id = '';
            }
            const studIdx = this.formData.alkalmassag.indexOf('tenyesztes');
            if (studIdx > -1) {
                this.formData.alkalmassag.splice(studIdx, 1);
            }
        }
    },

    onGenderChange() {
        if (this.formData.htipus === 'fedezomen' && this.formData.nem_id !== 'stallion') {
            this.formData.nem_id = 'stallion';
        }
    },

    // ── Passport verification ─────────────────────────────────────────────────

    async verifyPassport() {
        if (!this.formData.passportNumber) return;
        this.verifying = true;
        await new Promise(resolve => setTimeout(resolve, 1000));
        this.passportVerified = true;
        this.verifying = false;
    },

    // ── Navigation ───────────────────────────────────────────────────────────

    scrollToWizardTop() {
        this.$nextTick(() => window.scrollTo({ top: 0, behavior: 'auto' }));
    },

    goToStep(stepNumber) {
        if (this.submitting || stepNumber >= this.currentStep) return;
        this.currentStep = stepNumber;
        this.validationErrors = [];
        this.scrollToWizardTop();
    },

    nextStep() {
        if (!this.validateStep()) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        if (this.currentStep < this.totalSteps) {
            this.validationErrors = [];
            if (this.skipsPackageStepOnEdit && this.currentStep === (this.listingMode === 'horseface' ? 3 : 4)) {
                this.currentStep = this.totalSteps;
            } else {
                this.currentStep++;
            }
            this.scrollToWizardTop();
        } else {
            this.submitListing();
        }
    },

    prevStep() {
        this.validationErrors = [];
        if (this.currentStep > 1) {
            if (this.skipsPackageStepOnEdit && this.currentStep === this.totalSteps) {
                this.currentStep = this.listingMode === 'horseface' ? 3 : 4;
            } else {
                this.currentStep--;
            }
        } else {
            this.listingMode = '';
            this.currentStep = 1;
        }
        this.scrollToWizardTop();
    },

    // ── Submit ───────────────────────────────────────────────────────────────

    async submitListing() {
        if (this.submitting) {
            return;
        }

        this.submitting     = true;
        this.serverError    = '';
        this.uploadProgress = isHorseface(this.listingMode) ? 'Lóprofil mentése...' : 'Hirdetés mentése...';

        // HorseFace always needs a configured WC product before submit (cart path).
        if (requiresPaymentBeforePublish(this.listingMode)) {
            const hfPackageMap = typeof lovasokParams.horsefaceProducts !== 'undefined'
                ? lovasokParams.horsefaceProducts : {};
            const planObj = hfPackageMap[this.formData.horsefacePackage];
            if (!planObj?.id) {
                this.submitting = false;
                this.uploadProgress = '';
                this.serverError = 'A HorseFace előfizetés jelenleg nem elérhető. Próbáld újra később, vagy vedd fel a kapcsolatot az ügyfélszolgálattal.';
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }
        }

        let uploadBytes = 0;

        try {
            const formData = new FormData();
            formData.append('action', 'lovasok_create_listing');

            const scalarFields = [
                'htipus', 'hirdetes_cim', 'lo_neve', 'fajta_id', 'nem_id', 'foszin_id',
                'datum_ev', 'datum_ho', 'magassag_bot', 'magassag_szalag', 'kepzettseg_id',
                'versenyez', 'startszam', 'erv_loutlevel', 'lo_azon',
                'tisztaveru', 'torzskonyvi_szam',
                'apja', 'anyja', 'sire_sire', 'sire_dam', 'anyja_nagyapa', 'dam_dam',
                'sire_sire_sire', 'sire_sire_dam', 'sire_dam_sire', 'sire_dam_dam',
                'dam_sire_sire', 'dam_sire_dam', 'dam_dam_sire', 'dam_dam_dam',
                'leiras', 'ar_st', 'ar', 'penznem', 'arkatlista', 'ar_tipus',
                'csere', 'weboldal', 'telefon',
                'megtekint_orszag', 'megtekint_megye', 'megtekint_varos',
                'megtekint_kulfvaros', 'irsz', 'utca',
                'package', 'passportNumber',
                'video_url', 'video_url_2', 'video_url_3',
            ];

            scalarFields.forEach(key => {
                if (this.formData[key] !== undefined && this.formData[key] !== null) {
                    formData.append(key, this.formData[key]);
                }
            });

            formData.append('ve_dijugratas',  this.formData.ve_dijugratas  ? '1' : '0');
            formData.append('ve_dijlovaglas', this.formData.ve_dijlovaglas ? '1' : '0');
            formData.append('ve_fogathajtas', this.formData.ve_fogathajtas ? '1' : '0');
            formData.append('alkalmassag', JSON.stringify(this.formData.alkalmassag));

            if (this.uploadFiles.length > 0) {
                this.uploadProgress = `Képek feltöltése (${this.uploadFiles.length} db)...`;
            }
            this.uploadFiles.forEach((file, index) => formData.append(`images[${index}]`, file));

            if (this.uploadDocuments.length > 0) {
                this.uploadProgress = 'Dokumentumok hozzáadása...';
            }
            const docCounters = {};
            this.uploadDocuments.forEach(({ file, docType }) => {
                const fieldKey = `documents_${docType}`;
                const idx = docCounters[fieldKey] ?? 0;
                formData.append(`${fieldKey}[${idx}]`, file);
                docCounters[fieldKey] = idx + 1;
            });

            if (this.deletedAttachments.length > 0) {
                formData.append('deleted_attachments', JSON.stringify(this.deletedAttachments));
            }
            if (this.previewImages.length > 0 && this.previewImages[0].id) {
                formData.append('cover_attachment_id', String(this.previewImages[0].id));
            } else if (this.previewImages.length > 0 && this.uploadFiles.length > 0) {
                formData.append('cover_is_new_upload', '1');
            }
            if (this.isEditing && this.listingId) {
                formData.append('listing_id', this.listingId);
            }

            formData.append('nonce', lovasokParams.ajaxNonce || lovasokParams.nonce);

            uploadBytes = this.uploadFiles.reduce((sum, file) => sum + (file?.size || 0), 0);
            const maxPostBytes = Number(lovasokParams.maxPostBytes) || 0;
            if (maxPostBytes > 0 && uploadBytes + 524288 > maxPostBytes) {
                const message = `A képek együtt túl nagyok (${formatUploadSize(uploadBytes)}). A szerver egyszerre legfeljebb ${formatUploadSize(maxPostBytes)} adatot fogad. Törölj néhány képet, vagy tölts fel kisebbeket, majd próbáld újra.`;
                this.serverError = message;
                this.reportListingFailure(message, 0, '', uploadBytes, 'too_large');
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }

            this.uploadProgress = 'Adatok küldése a szerverhez...';
            const response = await fetch(lovasokParams.ajaxUrl, { method: 'POST', body: formData });

            this.uploadProgress = 'Válasz feldolgozása...';
            const responseText = await response.text();
            let result = null;
            try {
                result = responseText ? JSON.parse(responseText) : null;
            } catch (parseError) {
                result = null;
            }

            if (!result || typeof result.success === 'undefined') {
                const body = (responseText || '').trim();
                const droppedUpload = response.status === 400 && (body === '0' || body === '' || body === '-1');
                const tooLarge = response.status === 413 || response.status === 0 || droppedUpload;
                const message = tooLarge
                    ? 'A feltöltés túl nagy, a szerver nem fogadta el. Törölj néhány képet, vagy tölts fel kisebbeket, majd próbáld újra.'
                    : `A mentés nem sikerült (válasz: ${response.status || 'nincs'}). Próbáld újra, vagy tölts fel kevesebb képet.`;
                this.serverError = message;
                this.reportListingFailure(message, response.status, body, uploadBytes);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }

            if (result.success) {
                this.clearDraft();
                const listingId = result.data?.listing_id;
                const goDashboard = () => {
                    // Instant-publish path uses server redirect (may include listing_created=1).
                    const url = result.data?.redirect_url
                        || `${lovasokParams.myAccountUrl || `${lovasokParams.homeUrl}/fiokom`}?tab=horses`;
                    window.location.replace(url);
                };

                // Cart path — HorseFace subscription product.
                if (isHorseface(this.listingMode) && listingId) {
                    const hfPackageMap = typeof lovasokParams.horsefaceProducts !== 'undefined'
                        ? lovasokParams.horsefaceProducts : {};
                    const planObj = hfPackageMap[this.formData.horsefacePackage];
                    if (!planObj?.id) {
                        this.serverError = 'A HorseFace előfizetés jelenleg nem elérhető. A profil piszkozatként mentve lett — próbáld újra később.';
                        if (listingId) {
                            this.isEditing = true;
                            this.listingId = listingId;
                        }
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                        return;
                    }
                    const cartUrl = new URL(lovasokParams.cartUrl);
                    cartUrl.searchParams.set('add-to-cart', planObj.id);
                    cartUrl.searchParams.set('listing_id', listingId);
                    window.location.replace(cartUrl.toString());
                    return;
                }

                // Cart path — paid marketplace package (server may already send cart redirect_url).
                if (result.data?.redirect_url) {
                    window.location.replace(result.data.redirect_url);
                    return;
                }
                if (!this.isEditing && this.formData.package) {
                    const cartUrl = new URL(lovasokParams.cartUrl);
                    cartUrl.searchParams.set('add-to-cart', this.formData.package);
                    cartUrl.searchParams.set('listing_id', listingId);
                    window.location.replace(cartUrl.toString());
                    return;
                }

                // Instant publish path — free marketplace listing → Fiókom.
                goDashboard();
            } else {
                const message = (typeof result.data === 'string' && result.data)
                    || result.data?.message
                    || 'Hiba történt a mentés során. Kérlek próbáld újra.';
                this.serverError = message;
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } catch (error) {
            console.error('Error submitting listing:', error);
            const message = 'A kapcsolat megszakadt mentés közben. Ha sok vagy nagy képet küldtél, próbáld kevesebbel újra.';
            this.serverError = message;
            this.reportListingFailure(message, 0, '', uploadBytes);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } finally {
            this.submitting     = false;
            this.uploadProgress = '';
        }
    },

    // ── Label helpers ─────────────────────────────────────────────────────────

    reportListingFailure(detail, status, responseText = '', uploadBytes = 0, reason = '') {
        try {
            const body = new FormData();
            body.append('action', 'lovasok_log_listing_error');
            body.append('nonce', lovasokParams.ajaxNonce || lovasokParams.nonce || '');
            body.append('detail', String(detail || '').slice(0, 500));
            body.append('http_status', String(status || 0));
            body.append('listing_id', String(this.listingId || ''));
            body.append('upload_bytes', String(uploadBytes || 0));
            body.append('response_excerpt', String(responseText || '').replace(/\s+/g, ' ').slice(0, 180));
            if (reason) body.append('reason', reason);
            fetch(lovasokParams.ajaxUrl, { method: 'POST', body });
        } catch (logError) {
            console.error('Listing error log failed:', logError);
        }
    },

    getOptionLabel(group, slug) {
        if (!slug || !window.lovasokParams?.formOptions?.[group]) return slug;
        return window.lovasokParams.formOptions[group][slug] || slug;
    },

    getSuitabilitiesLabel(slugs) {
        if (!Array.isArray(slugs) || slugs.length === 0) return '—';
        return slugs.map(slug => this.getOptionLabel('suitabilities', slug)).join(', ');
    },
});
